import { PayloadAction, createSlice } from '@reduxjs/toolkit'

type ChatUxState = {
  selectedFilesContentExpanded: Partial<Record<string, boolean>>
  promptAssistEnabled: boolean
  promptAssistLastFetched: number | null
  promptAssistFetchIsLoading: boolean
  defaultJurisdiction: string | null
  activeConversationId: string | null
}

const initialState: ChatUxState = {
  selectedFilesContentExpanded: {},
  promptAssistEnabled: true,
  promptAssistLastFetched: null,
  promptAssistFetchIsLoading: false,
  defaultJurisdiction: null,
  activeConversationId: null,
}

/**
 * Chat UX Slice
 * A slice to hold state for purely UX state that should not be lost when components are unmounted
 */
export const chatUxSlice = createSlice({
  name: 'chatUxState',
  initialState,
  reducers: {
    nullifyData: () => initialState,

    setSelectedFilesContentExpanded: (state, action: PayloadAction<{ conversationId: string; expanded: boolean }>) => {
      const { conversationId, expanded } = action.payload
      state.selectedFilesContentExpanded[conversationId] = expanded
    },

    toggleSelectedFilesContentExpanded: (state, action: PayloadAction<{ conversationId: string }>) => {
      const { conversationId } = action.payload
      const current = state.selectedFilesContentExpanded[conversationId] ?? false
      state.selectedFilesContentExpanded[conversationId] = !current
    },

    setPromptAssistEnabled: (state, action: PayloadAction<{ enabled: boolean }>) => {
      const { enabled } = action.payload
      state.promptAssistEnabled = enabled
    },

    setPromptAssistLastFetched: (state, action: PayloadAction<{ lastFetched: number }>) => {
      const { lastFetched } = action.payload
      state.promptAssistLastFetched = lastFetched
    },

    setPromptAssistFetchIsLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      const { isLoading } = action.payload
      state.promptAssistFetchIsLoading = isLoading
    },

    setDefaultJurisdiction: (state, action: PayloadAction<{ jurisdiction: string | null }>) => {
      state.defaultJurisdiction = action.payload.jurisdiction
    },

    setActiveConversationId: (state, action: PayloadAction<{ conversationId: string | null }>) => {
      const { conversationId } = action.payload
      state.activeConversationId = conversationId
    },
  },
})

// Actions
export const {
  nullifyData,
  setSelectedFilesContentExpanded,
  toggleSelectedFilesContentExpanded,
  setPromptAssistEnabled,
  setPromptAssistLastFetched,
  setPromptAssistFetchIsLoading,
  setDefaultJurisdiction,
  setActiveConversationId,
} = chatUxSlice.actions

export default chatUxSlice.reducer
